import { Box, Button, Grid, styled, Typography } from "@mui/material";
import { Space } from "~/lib/types";
import { useColorMode } from "~/providers/color-mode";
import SelectFrameworks from "../../../images/compliance-entry-page/select-frameworks.svg";
import SelectFrameworksLight from "../../../images/compliance-entry-page/select-frameworks-light.svg";
import ScanInfra from "../../../images/compliance-entry-page/scan-infra.svg";
import ScanInfraLight from "../../../images/compliance-entry-page/scan-infra-light.svg";
import ExportReports from "../../../images/compliance-entry-page/export-reports.svg";
import ExportReportsLight from "../../../images/compliance-entry-page/export-reports-light.svg";
import { motion } from "motion/react";
import { SOCIcon } from "~/images/compliance-entry-page/SOCIcon";
import { CISIcon } from "~/images/compliance-entry-page/CISIcon";
import { BsiIcon } from "~/images/compliance-entry-page/BsiIcon";
import { CsaIcon } from "~/images/compliance-entry-page/CsaIcon";
import { ISOIcon } from "~/images/compliance-entry-page/ISOIcon";
import { HIPAAIcon } from "~/images/compliance-entry-page/HIPAAIcon";
import { NistIcon } from "~/images/compliance-entry-page/NistIcon";
import { PCIIcon } from "~/images/compliance-entry-page/PCIIcon";
import { VdaIcon } from "~/images/compliance-entry-page/VdaIcon";
import { CanSetBundle } from "~/lib/iam/actions/cnspec/policy/PolicyHub/SetBundle";

type ComplianceEntrySplashProps = {
  space: Space;
  setIsAdding: (isAdding: boolean) => void;
};

const icons = [
  { id: "SOCIcon", mark: <SOCIcon sx={{ fontSize: 48 }} /> },
  { id: "BsiIcon", mark: <BsiIcon sx={{ fontSize: 48 }} /> },
  { id: "CISIcon", mark: <CISIcon sx={{ fontSize: 48 }} /> },
  { id: "CsaIcon", mark: <CsaIcon sx={{ fontSize: 48 }} /> },
  { id: "ISOIcon", mark: <ISOIcon sx={{ fontSize: 48 }} /> },
  { id: "HIPAAIcon", mark: <HIPAAIcon sx={{ fontSize: 48 }} /> },
  { id: "NistIcon", mark: <NistIcon sx={{ fontSize: 48 }} /> },
  { id: "PCIIcon", mark: <PCIIcon sx={{ fontSize: 48 }} /> },
  { id: "VdaIcon", mark: <VdaIcon sx={{ fontSize: 48 }} /> },
];

export function ComplianceSplash({
  space,
  setIsAdding,
}: ComplianceEntrySplashProps) {
  const { mode } = useColorMode();
  return (
    <Box
      id="compliance-splash-screen"
      m="auto"
      mt={0}
      mb={5}
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      sx={{ width: 1 }}
    >
      <Typography
        variant="h5"
        color="text.secondary"
        fontWeight={700}
        lineHeight="48px"
        textAlign="center"
        mb={2}
      >
        Streamline your compliance process with the Mondoo Compliance Hub
      </Typography>
      <Grid
        container
        columnSpacing={3}
        justifyContent="center"
        alignItems="center"
      >
        {icons.map((icon) => (
          <Grid key={icon.id}>{icon.mark}</Grid>
        ))}
      </Grid>
      <Grid
        container
        justifyContent="center"
        alignItems="flex-start"
        columnSpacing={{ sm: 3 }}
        rowSpacing={{ xs: 8, sm: 2 }}
        py={10}
      >
        <Grid
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          size={{
            xs: 12,
            sm: 4,
          }}
        >
          <Box
            component="img"
            src={mode === "light" ? SelectFrameworksLight : SelectFrameworks}
            alt="Select compliance frameworks to enable automatic evidence gathering"
            width="auto"
            maxHeight={144}
            mb={2}
          />
          <StyledText>
            Select compliance frameworks to enable automatic evidence gathering.
          </StyledText>
        </Grid>
        <Grid
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          size={{
            xs: 12,
            sm: 4,
          }}
        >
          <Box
            component="img"
            src={mode === "light" ? ScanInfraLight : ScanInfra}
            alt="Set up your integrations to scan your infrastructure"
            width="auto"
            maxHeight={144}
            mb={2}
          />
          <StyledText>
            Set up your integrations to scan your infrastructure.
          </StyledText>
        </Grid>
        <Grid
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          size={{
            xs: 12,
            sm: 4,
          }}
        >
          <Box
            component="img"
            src={mode === "light" ? ExportReportsLight : ExportReports}
            alt="Export reports to ace your audit"
            width="auto"
            maxHeight={144}
            mb={2}
          />
          <StyledText>
            Export reports to ace your audit. Never manually gather evidence
            again.
          </StyledText>
        </Grid>
      </Grid>
      <CanSetBundle>
        <Button
          data-name="framework-add-first-integration"
          onClick={() => setIsAdding(true)}
          sx={{
            display: "block",
            ml: "auto",
            mr: "auto",
            px: 2,
            background: (theme) => theme.palette.primary.gradient,
            color: "common.white",
          }}
        >
          SELECT COMPLIANCE FRAMEWORK
        </Button>
      </CanSetBundle>
    </Box>
  );
}

const StyledText = styled(Typography)`
  color: ${(p) => p.theme.palette.text.secondary};
  font-weight: 600;
  line-height: 24px;
  text-align: center;
`;
